import type {
  SuccessFeeMigrationStartBlockedReason,
  SuccessFeeMigrationStatus,
} from '@fluenceur/types';

/**
 * Why the API says an attempt cannot be started, as the sentence a merchant reads.
 *
 * The keys are written in full so the i18n orphan check sees them, and the
 * `satisfies` guard makes a new reason in the contract a typecheck failure rather
 * than a silently missing sentence.
 *
 * `pending_attempt` is deliberately absent: an attempt already waiting for
 * approval is not something to warn an admin about, and the POST is idempotent.
 */
const BLOCKED_REASON_KEY = {
  terms_not_accepted: 'errors.E_SUCCESS_FEE_MIGRATION_TERMS_NOT_ACCEPTED',
  no_active_subscription: 'errors.E_SUCCESS_FEE_MIGRATION_NO_ACTIVE_SUBSCRIPTION',
  currency_mismatch: 'errors.E_SUCCESS_FEE_MIGRATION_CURRENCY_MISMATCH',
  integration_missing: 'errors.E_SUCCESS_FEE_MIGRATION_INTEGRATION_MISSING',
} satisfies Record<Exclude<SuccessFeeMigrationStartBlockedReason, 'pending_attempt'>, string>;

/**
 * The i18n key of the sentence to show an admin in place of the call to action,
 * or null when the action is worth offering.
 *
 * Offering a button the API will refuse costs the merchant a round trip and a
 * red toast, so a known blocker becomes a sentence instead. It is never a
 * disabled button: a disabled control says nothing about why.
 */
export const startBlockedReasonKey = (status: SuccessFeeMigrationStatus): string | null => {
  if (status.canStart) return null;

  const reason = status.startBlockedReason;
  if (!reason || reason === 'pending_attempt') return null;

  return BLOCKED_REASON_KEY[reason];
};
